import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Star, MapPin, Car, Clock, CheckCircle2, MessageSquare } from "lucide-react";
import AvailabilityCalendar from "../components/AvailabilityCalendar";
import MapEmbed from "../components/MapEmbed";
import RouteMap from "../components/RouteMap";
import DateTimePicker from "../components/DateTimePicker";
import { toast } from "sonner";
import Seo from "../components/Seo";
import VerifiedBadge from "../components/VerifiedBadge";
import AddressAutocomplete from "../components/AddressAutocomplete";

// Map trainer province to ISO 3166-2 subdivision code (falls back to CA).
const PROVINCE_CODES = {
  Ontario: "ON", "Québec": "QC", Quebec: "QC", "British Columbia": "BC",
  Alberta: "AB", Manitoba: "MB", Saskatchewan: "SK", "Nova Scotia": "NS",
  "New Brunswick": "NB", "Newfoundland and Labrador": "NL",
  "Prince Edward Island": "PE", "Northwest Territories": "NT",
  Nunavut: "NU", Yukon: "YT",
};

export default function TrainerDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [t, setT] = useState(null);
  const [hours, setHours] = useState(1);
  const [transmission, setTransmission] = useState("auto");
  const [date, setDate] = useState("");
  const [pickup, setPickup] = useState("");
  const [refCode, setRefCode] = useState("");
  const [useCredits, setUseCredits] = useState(false);
  const [myReferral, setMyReferral] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    api.get(`/trainers/${id}`).then(r => {
      setT(r.data);
      setTransmission(r.data.transmissions?.[0] || "auto");
    });
  }, [id]);

  useEffect(() => {
    if (user?.role === "learner") api.get("/learner/referral").then(r => setMyReferral(r.data)).catch(() => {});
  }, [user]);

  const pickupReady = pickup.trim().length >= 6;

  const book = async (kind, packageId) => {
    if (!user) return nav("/login");
    if (user.role !== "learner") return toast.error("Switch to a learner account to book");
    // Pickup is now required so the instructor can measure the drive to you.
    if (!pickupReady) {
      toast.error("Add your pickup address (street, city and province) before booking");
      document.querySelector('[data-testid="pickup-input"]')?.focus();
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/bookings", {
        trainer_id: id,
        booking_type: kind,
        package_id: packageId,
        scheduled_at: date || null,
        hours: kind === "session" ? Number(hours) : null,
        transmission,
        pickup_location: pickup.trim(),
        referral_code: refCode || undefined,
        use_credits: useCredits,
      });
      nav(`/booking/${data.booking_id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Could not create booking");
    }
    setLoading(false);
  };

  if (!t) return <div className="max-w-6xl mx-auto p-12 text-muted-foreground">Loading...</div>;

  const total = (Number(t.hourly_rate) * Number(hours || 1)).toFixed(2);
  const zone = t.zone_city || t.zone || "Canada";
  const province = t.zone_province || t.province || "";
  const provCode = PROVINCE_CODES[province] || "";
  const tx = (t.transmissions && t.transmissions.length)
    ? t.transmissions.join(" / ")
    : "auto";
  const rating = t.rating || t.avg_rating;
  const reviewCount = t.review_count || t.reviews_count || 0;
  const trainerJsonLd = {
    "@context": "https://schema.org",
    "@type": "LocalBusiness",
    "@id": `https://driveterrain.ca/trainers/${id}`,
    name: t.name,
    image: t.photo_url || undefined,
    url: `https://driveterrain.ca/trainers/${id}`,
    telephone: t.phone || undefined,
    priceRange: `CA$${t.hourly_rate}/hr`,
    address: { "@type": "PostalAddress", addressLocality: zone,
                 addressRegion: province || undefined, addressCountry: "CA" },
    areaServed: zone,
    ...(rating && reviewCount ? {
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: Number(rating).toFixed(1),
        reviewCount: Number(reviewCount),
      },
    } : {}),
    makesOffer: {
      "@type": "Offer",
      itemOffered: {
        "@type": "Service",
        name: `${tx} driving lessons in ${zone}`,
        serviceType: "Driving instruction",
      },
      priceCurrency: "CAD",
      price: t.hourly_rate,
    },
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="trainer-detail">
      <Seo
        title={`${t.name} – Driving Instructor in ${zone} · DriveTerrain`}
        description={`Book ${t.name}, a verified driving instructor in ${zone}. ${tx} lessons from CA$${t.hourly_rate}/hr.${rating && reviewCount ? ` ${Number(rating).toFixed(1)}★ (${reviewCount} reviews).` : ""}`}
        keywords={`${t.name}, driving instructor ${zone}, ${tx} driving lessons, ${zone} road test prep`}
        canonical={`/trainers/${id}`}
        ogType="profile"
        ogImage={t.photo_url || undefined}
        ogImageAlt={`Portrait of driving instructor ${t.name}`}
        geoRegion={provCode ? `CA-${provCode}` : "CA"}
        geoPlacename={zone}
        jsonLd={trainerJsonLd}
      />
      <div className="grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-7 space-y-8">
          <div className="flex flex-col sm:flex-row items-start gap-4 sm:gap-6">
            <div className="w-20 h-20 sm:w-28 sm:h-28 bg-muted overflow-hidden flex-shrink-0 border border-border">
              {t.photo_url && <img src={t.photo_url} alt={t.name} className="w-full h-full object-cover" />}
            </div>
            <div className="min-w-0">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-primary">Verified Trainer</div>
              <h1 className="text-3xl md:text-4xl font-display font-semibold tracking-tight mt-1 flex flex-wrap items-center gap-2" data-testid="trainer-name">
                {t.name}
                {t.verified && (
                  <VerifiedBadge size="md" testId="kyc-verified-chip" />
                )}
              </h1>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground mt-2">
                {t.review_count > 0 && t.rating != null ? (
                  <span className="flex items-center gap-1" data-testid="hero-rating">
                    <Star className="w-4 h-4 fill-primary text-primary" />
                    <span className="font-medium text-foreground">{Number(t.rating).toFixed(1)}</span>
                    <span>({t.review_count} review{t.review_count === 1 ? "" : "s"})</span>
                  </span>
                ) : (
                  <span className="italic" data-testid="hero-rating">New instructor · No reviews yet</span>
                )}
                <span>·</span>
                <span className="flex items-center gap-1"><MapPin className="w-4 h-4" /> {t.zone}</span>
                <span>·</span>
                <span>{t.years_experience}+ yrs</span>
              </div>
            </div>
          </div>

          <div className="border border-border bg-card p-6">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">About</div>
            <p className="mt-2 leading-relaxed">{t.bio}</p>
            <div className="grid grid-cols-2 gap-4 mt-6 text-sm">
              <div><div className="text-muted-foreground">Car</div><div className="font-medium">{t.car_model}</div></div>
              <div><div className="text-muted-foreground">Transmissions</div><div className="font-medium">{t.transmissions?.join(", ")}</div></div>
              <div><div className="text-muted-foreground">Car types</div><div className="font-medium">{t.car_types?.join(", ")}</div></div>
              <div><div className="text-muted-foreground">Available days</div><div className="font-medium">{t.availability?.join(", ")}</div></div>
            </div>
            {t.equipment?.length > 0 && (
              <div className="mt-5 pt-5 border-t border-border">
                <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2">Equipment specialization</div>
                <div className="flex flex-wrap gap-2">
                  {t.equipment.map(eq => (
                    <span key={eq} className="px-2.5 py-1 text-xs border border-primary/40 bg-primary/5 text-foreground" data-testid={`equipment-badge-${eq}`}>
                      {eq}
                      {(t.equipment_transmissions?.[eq] || []).length > 0 && (
                        <span className="text-muted-foreground"> · {(t.equipment_transmissions[eq] || []).join("/")}</span>
                      )}
                    </span>
                  ))}
                </div>
              </div>
            )}
            {t.languages?.length > 0 && (
              <div className="mt-4 text-xs text-muted-foreground">
                Speaks: {t.languages.map(l => l === 'en' ? 'English' : 'Français').join(", ")}
              </div>
            )}
          </div>

          {t.packages?.length > 0 && (
            <div>
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Packages</div>
              <div className="grid sm:grid-cols-2 gap-4">
                {t.packages.map(p => (
                  <div key={p.package_id} className="border border-border bg-card p-5 flex flex-col" data-testid={`package-${p.package_id}`}>
                    <div className="font-display text-lg font-medium">{p.title}</div>
                    <div className="text-sm text-muted-foreground mt-1 flex-1">{p.description}</div>
                    <div className="flex items-center justify-between mt-4 pt-4 border-t border-border">
                      <div>
                        <div className="text-xs uppercase tracking-widest text-muted-foreground">{p.hours} hours</div>
                        <div className="font-display text-2xl font-semibold">${p.price}</div>
                      </div>
                      <Button size="sm" className="rounded-sm" onClick={() => book("package", p.package_id)} disabled={loading || !pickupReady} data-testid={`book-pkg-${p.package_id}`}>Book package</Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-3">Availability</div>
            <AvailabilityCalendar availableDays={t.availability} busySlots={t.busy_slots} />
          </div>

          <MapEmbed city={t.zone?.split("·").slice(-1)[0]?.trim() || t.zone} zone={t.zone} />

          {pickup && pickup.trim() && (
            <RouteMap
              pickup={{ label: pickup.trim() }}
              destination={{ label: t.zone }}
              travelMode="DRIVING"
            />
          )}

          <div>
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground flex items-center gap-2">
                <MessageSquare className="w-3 h-3" /> Reviews ({t.review_count || t.reviews?.length || 0})
              </div>
              <div className="flex items-center gap-1 text-sm"><Star className="w-4 h-4 fill-primary text-primary" /> {t.rating?.toFixed?.(1) || "—"}</div>
            </div>
            {(!t.reviews || t.reviews.length === 0) ? (
              <div className="border border-dashed border-border p-6 text-center text-sm text-muted-foreground" data-testid="no-reviews">No reviews yet. Be the first to share your experience.</div>
            ) : (
              <div className="space-y-3">
                {t.reviews.slice(0, 6).map(r => (
                  <div key={r.review_id} className="border border-border bg-card p-4" data-testid={`review-${r.review_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="font-medium">{r.learner_name || "Learner"}</div>
                      <div className="flex">
                        {[1,2,3,4,5].map(n => <Star key={n} className={`w-3.5 h-3.5 ${n <= r.rating ? "fill-primary text-primary" : "text-muted-foreground"}`} />)}
                      </div>
                    </div>
                    {r.comment && <div className="text-sm text-muted-foreground mt-1.5">{r.comment}</div>}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <aside className="lg:col-span-5 lg:sticky lg:top-20 self-start">
          <div className="border border-border bg-card p-6">
            <div className="flex items-baseline justify-between">
              <div>
                <div className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground">Hourly rate · CAD</div>
                <div className="font-display text-4xl font-semibold mt-1" data-testid="hourly-rate">${t.hourly_rate}</div>
              </div>
              <CheckCircle2 className="text-primary w-6 h-6" />
            </div>
            <div className="mt-6 space-y-4">
              <div>
                <Label>Hours</Label>
                <Input type="number" min={1} max={10} value={hours} onChange={e => setHours(e.target.value)} className="rounded-sm" data-testid="hours-input" />
              </div>
              <div>
                <Label>Preferred date & time</Label>
                <div className="mt-1">
                  <DateTimePicker
                    value={date}
                    onChange={setDate}
                    allowedSlots={(() => {
                      if (!date || !t.time_slots) return undefined;
                      try {
                        const d = new Date(date);
                        const key = ["sun","mon","tue","wed","thu","fri","sat"][d.getDay()];
                        return t.time_slots[key] || [];
                      } catch { return undefined; }
                    })()}
                    testId="date-input"
                    placeholder="Pick date & time"
                  />
                </div>
                {t.time_slots && Object.values(t.time_slots).some(arr => arr?.length) && (
                  <p className="text-[11px] text-muted-foreground mt-1.5" data-testid="date-input-hint">
                    Trainer&apos;s open hours are highlighted; closed days are disabled.
                  </p>
                )}
              </div>
              <div>
                <Label>Transmission</Label>
                <select value={transmission} onChange={e => setTransmission(e.target.value)} className="w-full border border-border bg-background rounded-sm h-10 px-3 text-sm mt-1" data-testid="transmission-select">
                  {t.transmissions?.map(x => <option key={x} value={x}>{x}</option>)}
                </select>
              </div>
              <div>
                <Label>Pickup location</Label>
                <AddressAutocomplete value={pickup} onChange={setPickup}
                  placeholder="e.g. 23 Market St, Toronto, ON" required minLength={6}
                  className="rounded-sm" testId="pickup-input" />
                <div className="text-xs text-muted-foreground mt-1.5">
                  Include the street, city and province — your instructor uses this to plan the drive to you.
                </div>
              </div>
              <div>
                <Label>Referral code (optional)</Label>
                <Input value={refCode} onChange={e => setRefCode(e.target.value.toUpperCase())} placeholder="$10 off first booking" className="rounded-sm font-mono tracking-widest" data-testid="referral-code-input" />
              </div>
              {user?.role === "learner" && myReferral?.credits > 0 && (
                <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="use-credits-row">
                  <Checkbox checked={useCredits} onCheckedChange={(v) => setUseCredits(!!v)} data-testid="use-credits-check" />
                  Use ${myReferral.credits.toFixed(2)} credit
                </label>
              )}
            </div>
            <div className="mt-6 pt-6 border-t border-border flex items-center justify-between">
              <div>
                <div className="text-xs uppercase tracking-widest text-muted-foreground">Total</div>
                <div className="font-display text-3xl font-semibold" data-testid="total-amount">${total}</div>
              </div>
              <Button className="rounded-sm h-11 px-6" onClick={() => book("session")} disabled={loading || !pickupReady} data-testid="book-session-btn">
                {loading ? "..." : pickupReady ? "Book session" : "Add pickup address"}
              </Button>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
